import React, { FC, useEffect, useMemo } from "react";

import {
  currentFolderRootBreadcrumbContext,
  currentLocaleAtom,
  NO_FACETS_COUNT,
  useFolderTree,
} from "@keepeek/commons";
import {
  KImageWidget as KImageWidgetComponent,
  KImageWidgetAnimationType,
  KImageWidgetProps as KImageWidgetComponentsProps,
  mosaicCurrentSelectedElement,
  useBreadcrumb,
} from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import { isLocalURL } from "next/dist/shared/lib/router/utils/is-local-url";
import { useRecoilValue, useSetRecoilState } from "recoil";
import sanitizeHtml from "sanitize-html";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { FolderBrowse } from "../../../models/configuration/components/folderBrowse";
import {
  KImageWidget as KImageWidgetModel,
  KImageWidgetURL,
  LinkType,
  Resource,
} from "../../../models/configuration/components/kImageWidget";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../../../providers/config/hooks";
import { useInitConfig } from "../../../providers/config/hooks/init";
import { useImageOptim } from "../../../providers/config/hooks/useImageOptim";
import ImageOptim from "../../ImageOptim";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { ComponentNextLink } from "../utils/parseInternalUrl";

export type KImageWidgetProps = {
  widgetId: KImageWidgetModel["id"];
  image?: KImageWidgetModel["kImageWidgetimage"];
  url?: KImageWidgetURL;
} & Pick<KImageWidgetComponentsProps, "backgroundColor" | "position" | "url"> &
  KWidgetProps;

const KImageWidget: FC<React.PropsWithChildren<KImageWidgetProps>> = function ({
  backgroundColor = "transparent",
  image,
  position,
  url,
  widgetId,
  type,
}) {
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });

  const currentSelectedId = useRecoilValue(mosaicCurrentSelectedElement(widgetId));
  const setCurrentFolderId = useSetRecoilState(currentFolderRootBreadcrumbContext);
  const { initConfig } = useInitConfig();
  const { setActiveFilters } = useActiveFiltersFromURL();
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });
  const { showSubFolderLink = false } = useConfiguration<FolderBrowse>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_FOLDER_BROWSE,
  ) ?? {
    showSubFolderLink: false,
  };

  const { search } = useBreadcrumb({
    initFolderId: currentSelectedId,
    initFolderRootId: currentSelectedId,
    initialShowFolders: false,
    key: `useMosaic-${widgetId}`,
    resetSearch: true,
    clientIp: initConfig?.clientIp,
    showSubFolderLink,
    setActiveFilters,
    sentryCallback,
  });

  const { folderTree } = useFolderTree({
    folderId: url?.linkType === LinkType.Folder && url.folderId ? url?.folderId : undefined,
  });
  if (url?.linkType === LinkType.Folder && url.folderId) {
    const handleClickLink = () => {
      setCurrentFolderId(undefined); // Remove current folderId to get folder id from url
      search({
        folderId: url?.folderId ?? -1,
        extraParams: { fc: url?.folderId },
        folderTitle: folderTree?.folder.title,
      });
    };
    url = { ...url, handleClickLink };
  }

  url = { ...url, ComponentNextLink };

  if (
    url?.href &&
    url?.linkType === LinkType.URL &&
    isLocalURL(url?.href) &&
    !url?.href.includes(NO_FACETS_COUNT)
  ) {
    url = { ...url, href: `${url?.href}&${NO_FACETS_COUNT}=true` };
  }

  const currentLocale = useRecoilValue(currentLocaleAtom);
  let imageUrl = image && image.url?.find((u) => u.language === currentLocale)?.value;
  const resource: Resource | undefined = image?.url?.find(
    (u) => u.language === currentLocale,
  )?.resource;
  if (resource && resource.url) {
    imageUrl = resource.url;
  }

  const { loading, shouldOptimize } = useImageOptim();

  const translatedImage: KImageWidgetComponentsProps["image"] = useMemo(
    () => ({
      url: imageUrl,
      alt:
        (image && image.alt?.find((a) => a.language === currentLocale)?.value) ||
        (image && image.alt?.[0]?.value),
      minHeight: image && image.minHeight,
      borderRadius: image && image.borderRadius,
      animationType:
        (image &&
          image.animation &&
          image.animation.animationType &&
          (image.animation.animationType as unknown as KImageWidgetAnimationType)) ||
        KImageWidgetAnimationType.NoAnimation,
    }),
    [image, imageUrl, currentLocale],
  );

  useEffect(() => {
    // Do not render if we do not have at least:
    if (!(!!backgroundColor || !!translatedImage.url)) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [translatedImage, setWidgetState, backgroundColor]);

  if (widgetState.state === WidgetUIState.RENDERED) {
    return (
      <>
        {!loading && (
          <KImageWidgetComponent
            position={position}
            url={url}
            image={translatedImage}
            sx={{ position: "relative", minHeight: translatedImage.minHeight }}
            imageOverride={
              !!translatedImage.url &&
              shouldOptimize && (
                <ImageOptim
                  src={translatedImage.url}
                  alt={sanitizeHtml(translatedImage.alt ?? "")}
                  style={{
                    minHeight: translatedImage.minHeight,
                    objectFit: position?.cover ? "cover" : "contain",
                    objectPosition: `${position?.horizontal ?? "center"} ${
                      position?.vertical ?? "center"
                    }`,
                  }}
                  fill
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    onContextMenu={(e) => e.preventDefault()}
                    src={translatedImage.url}
                    alt={sanitizeHtml(translatedImage.alt ?? "")}
                    style={{
                      minHeight: translatedImage.minHeight,
                      objectFit: position?.cover ? "cover" : "inherit",
                      objectPosition: `${position?.horizontal ?? "center"} ${
                        position?.vertical ?? "center"
                      }`,
                    }}
                  />
                </ImageOptim>
              )
            }
          />
        )}
      </>
    );
  }
  return null;
};

export default KImageWidget;
